/**
 * Live state of the match this tab is looking at: the fog-filtered view (or lobby summary), the
 * static terrain map, the live event feed and connection status. One match at a time.
 */
import { create } from 'zustand';
import type { Command, GameEvent, GameMap, MatchSummary, PlayerId, PlayerView } from '@seven-dawns/shared';
import { ApiError } from '../net/api';
import type { Backend, MatchTransport } from '../net/backend';
import type { MatchSession } from '../net/session';
import type { CachedView } from '../net/viewCache';
import type { CommandAck, ConnectionStatus } from '../net/socket';
import { announce } from './toastStore';

const MAX_FEED = 300;

export interface LobbyState {
  summary: MatchSummary;
  you: PlayerId;
}

export interface MatchStoreState {
  backend: Backend | null;
  session: MatchSession | null;
  status: ConnectionStatus;
  view: PlayerView | null;
  /** Lobby summary when the server can't build a view yet (lobby phase). */
  lobby: LobbyState | null;
  map: GameMap | null;
  /** Live events received this session, newest last (the view also carries recent history). */
  feed: GameEvent[];
  /** Server clock minus local clock, for accurate countdowns. */
  clockOffsetMs: number;
  /** Local time the latest view/lobby arrived ("Showing the realm as of 14:20"). */
  receivedAtMs: number | null;
  /** Local time the connection dropped (for the 30 s reconnecting → offline threshold). */
  disconnectedAtMs: number | null;
  fatalError: string | null;
  mapError: string | null;
  /**
   * The view is this device's cached copy (the server couldn't be reached on load). Read-only:
   * sending is blocked anyway while the connection isn't open.
   */
  stale: boolean;

  connect(session: MatchSession, backend: Backend): void;
  disconnect(): void;
  send(cmd: Command): Promise<CommandAck>;
  retryMap(): void;
}

let transport: MatchTransport | null = null;
let mapAbort: AbortController | null = null;
/** Cached copy loaded for the current match, used only until a live view arrives. */
let cached: CachedView | null = null;
let cacheTimer: ReturnType<typeof setTimeout> | null = null;
const CACHE_EVERY_MS = 2000;

export const useMatchStore = create<MatchStoreState>((set, get) => {
  async function loadMap() {
    const { backend, session } = get();
    if (!backend || !session || mapAbort) return;
    mapAbort = new AbortController();
    try {
      const map = await backend.loadMap(session, mapAbort.signal);
      if (get().session?.code === session.code) {
        set({ map, mapError: null });
        scheduleCacheSave();
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      set({ mapError: error instanceof ApiError ? error.message : 'Could not load the map.' });
    } finally {
      mapAbort = null;
    }
  }

  /** Show the cached view if the server is unreachable and nothing live has arrived yet. */
  function showCacheIfOffline() {
    const s = get();
    if (
      !cached ||
      s.view ||
      s.lobby ||
      s.status === 'open' ||
      s.status === 'connecting' ||
      s.status === 'idle'
    )
      return;
    if (cached.view.code !== s.session?.code.toUpperCase()) return;
    set({ view: cached.view, map: s.map ?? cached.map, stale: true, receivedAtMs: cached.savedAtMs });
  }

  /** Save the latest live view (and map) for this match, at most every couple of seconds. */
  function scheduleCacheSave() {
    if (cacheTimer) return;
    cacheTimer = setTimeout(() => {
      cacheTimer = null;
      const { backend, session, view, map, stale, receivedAtMs } = get();
      if (!backend?.viewCache || !session || !view || stale) return;
      void backend.viewCache.save(session.code, { view, map, savedAtMs: receivedAtMs ?? Date.now() });
    }, CACHE_EVERY_MS);
  }

  return {
    backend: null,
    session: null,
    status: 'idle',
    view: null,
    lobby: null,
    map: null,
    feed: [],
    clockOffsetMs: 0,
    receivedAtMs: null,
    disconnectedAtMs: null,
    fatalError: null,
    mapError: null,
    stale: false,

    connect(session, backend) {
      const current = get();
      if (current.session?.code === session.code && current.backend === backend && transport) return;
      get().disconnect();
      set({
        backend,
        session,
        view: null,
        lobby: null,
        map: null,
        feed: [],
        fatalError: null,
        mapError: null,
        receivedAtMs: null,
        disconnectedAtMs: null,
        stale: false,
      });
      cached = null;
      void backend.viewCache?.load(session.code).then((entry) => {
        if (get().session?.code !== session.code) return;
        cached = entry;
        showCacheIfOffline();
      });

      transport = backend.openMatch(session, {
        onView: (view) => {
          set({ view, lobby: null, receivedAtMs: Date.now(), stale: false });
          cached = null;
          scheduleCacheSave();
          // The terrain map only exists once the match has started; fetch it once.
          if (view.status !== 'lobby' && !get().map) void loadMap();
        },
        onLobby: (summary, you) => set({ lobby: { summary, you }, receivedAtMs: Date.now() }),
        onEvents: (events) => set((s) => ({ feed: [...s.feed, ...events].slice(-MAX_FEED) })),
        onStatus: (status) => {
          const prev = get().status;
          set({
            status,
            disconnectedAtMs:
              status === 'open'
                ? null
                : status === 'reconnecting'
                  ? (get().disconnectedAtMs ?? Date.now())
                  : get().disconnectedAtMs,
          });
          if (prev === 'open' && status === 'reconnecting')
            announce('Connection lost. Reconnecting…', 'assertive');
          showCacheIfOffline();
        },
        onClockOffset: (clockOffsetMs) => set({ clockOffsetMs }),
        onFatal: (error) => {
          set({ fatalError: error });
          backend.sessions.remove(session.code);
          void backend.viewCache?.remove(session.code);
        },
      });
      transport.connect();
    },

    disconnect() {
      transport?.close();
      transport = null;
      cached = null;
      if (cacheTimer) clearTimeout(cacheTimer);
      cacheTimer = null;
      mapAbort?.abort();
      mapAbort = null;
      set({
        session: null,
        status: 'idle',
        view: null,
        lobby: null,
        map: null,
        feed: [],
        receivedAtMs: null,
        disconnectedAtMs: null,
        stale: false,
      });
    },

    send(cmd) {
      if (!transport) return Promise.resolve({ ok: false, error: 'Not connected to a match.' });
      return transport.send(cmd);
    },

    retryMap() {
      set({ mapError: null });
      void loadMap();
    },
  };
});

/** The current view; screens inside the match gate can rely on it being present. */
export function useView(): PlayerView {
  const view = useMatchStore((s) => s.view);
  if (!view) throw new Error('useView() used outside a loaded match');
  return view;
}
